import React, { useContext, useState } from 'react';
import PageLayout from '../../page-layout';
import SlidePage from '../../organisim/slide-page';
import { AppContext } from '../../../app';
import { useUser } from '../../../lib/hooks/use-user';
import { usePrivateLessonBookings, usePrivateLessonSlots } from '../../../lib/hooks/use-private-lessons';
import { createBulkPrivateLessonSlots, deletePrivateLessonSlot } from '../../../lib/fetchers/private-lessons';
import { useMutation, useQueryClient } from 'react-query';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Box, Flex } from '@rebass/grid';
import get from 'lodash.get';

const displayTime = (value) => new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
const dateKey = (date, timeZone) => {
  if (!timeZone) return date.toISOString().slice(0, 10);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};
const addDays = (value, days) => {
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};
const weekdays = [
  ['sunday', 'Su'], ['monday', 'M'], ['tuesday', 'Tu'], ['wednesday', 'W'],
  ['thursday', 'Th'], ['friday', 'F'], ['saturday', 'Sa'],
];

const PrivateLessonSchedule = () => {
  const { loggedInUserId } = useContext(AppContext);
  const { user } = useUser(loggedInUserId);
  const gymUid = get(user, 'gym.uuid');
  const gymTimeZone = get(user, 'gym.time_zone') || 'UTC';
  const initialDate = dateKey(new Date(), gymTimeZone);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [startDate, setStartDate] = useState(initialDate);
  const [endDate, setEndDate] = useState(addDays(initialDate, 6));
  const [selectedWeekdays, setSelectedWeekdays] = useState(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [durationMinutes, setDurationMinutes] = useState('30');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [notice, setNotice] = useState('');
  const filters = { startDate: new Date().toISOString().slice(0, 10), endDate: '2100-01-01' };
  const { slots, isLoading } = usePrivateLessonSlots(gymUid, filters);
  const { bookings } = usePrivateLessonBookings(gymUid, 'schedule');
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries(['private-lesson-slots', gymUid]);
    queryClient.invalidateQueries(['private-lesson-bookings', gymUid]);
  };
  const createBulk = useMutation(
    () => createBulkPrivateLessonSlots(gymUid, {
      start_date: startDate,
      end_date: endDate,
      weekdays: selectedWeekdays,
      start_time: startTime,
      end_time: endTime,
      duration_minutes: Number(durationMinutes),
      location,
      notes,
    }),
    {
      onSuccess: (result) => {
        setNotice(`${result.created_count} opening${result.created_count === 1 ? '' : 's'} created${result.skipped_count ? `; ${result.skipped_count} overlapping time${result.skipped_count === 1 ? '' : 's'} skipped` : ''}.`);
        setLocation('');
        setNotes('');
        setBulkOpen(false);
        refresh();
      },
      onError: (error) => setNotice(error.message),
    },
  );
  const remove = useMutation((slotId) => deletePrivateLessonSlot(gymUid, slotId), {
    onSuccess: () => { setNotice('Availability removed.'); refresh(); }, onError: (error) => setNotice(error.message),
  });

  return (
    <PageLayout selected="Private Lesson Schedule" pageTitle="Private Lesson Schedule">
      <Box py={2}>
        <Typography variant="h5">Private lesson availability</Typography>
        <Typography color="textSecondary">Create openings in bulk or manage individual times. Booked times are protected from removal.</Typography>
        {notice && <Typography color="primary" style={{ marginTop: 12 }}>{notice}</Typography>}
        <Card style={{ marginTop: 16 }}><CardContent>
          <Typography variant="h6">Open time for private lessons</Typography>
          <Typography color="textSecondary" style={{ marginTop: 8 }}>Generate duration-based openings on the days you teach, up to 30 days ahead.</Typography>
          <Button variant="contained" color="primary" style={{ marginTop: 16 }} onClick={() => setBulkOpen(true)}>Create openings</Button>
        </CardContent></Card>
        <Card style={{ marginTop: 16 }}><CardContent>
          <Typography variant="h6">My upcoming availability</Typography>
          {isLoading ? <Typography style={{ marginTop: 8 }}>Loading schedule...</Typography> : slots.length === 0 ? <Typography color="textSecondary" style={{ marginTop: 8 }}>No future availability yet.</Typography> : <List>{slots.map((slot) => <React.Fragment key={slot.id}><ListItem><ListItemText primary={`${displayTime(slot.starts_at)} – ${displayTime(slot.ends_at)}`} secondary={`${slot.status}${slot.location ? ` · ${slot.location}` : ''}`} /><Button color="secondary" disabled={slot.status === 'booked' || remove.isLoading} onClick={() => remove.mutate(slot.id)}>Remove</Button></ListItem><Divider /></React.Fragment>)}</List>}
        </CardContent></Card>
        <Card style={{ marginTop: 16 }}><CardContent>
          <Typography variant="h6">Booked lessons</Typography>
          {bookings.length === 0 ? <Typography color="textSecondary" style={{ marginTop: 8 }}>No booked lessons yet.</Typography> : <List>{bookings.filter((booking) => booking.status === 'booked').map((booking) => <React.Fragment key={booking.id}><ListItem><ListItemText primary={`${booking.member.name} · ${displayTime(booking.slot.starts_at)}`} secondary={booking.member_note || 'No member note'} /></ListItem><Divider /></React.Fragment>)}</List>}
        </CardContent></Card>
      </Box>
      <SlidePage open={bulkOpen} onClose={() => !createBulk.isLoading && setBulkOpen(false)} title="Create openings">
        <Typography color="textSecondary">Choose teaching days, the date range, and the daily appointment window. Existing times will be skipped.</Typography>
        <Box mt={3}>
          <Typography variant="subtitle1">Days you teach</Typography>
          <Flex flexWrap="wrap" style={{ gap: 4, marginTop: 8 }}>{weekdays.map(([value, label]) => <FormControlLabel key={value} style={{ marginRight: 0 }} control={<Checkbox color="primary" checked={selectedWeekdays.includes(value)} onChange={() => setSelectedWeekdays((days) => days.includes(value) ? days.filter((day) => day !== value) : [...days, value])} />} label={label} />)}</Flex>
        </Box>
        <Flex flexWrap="wrap" style={{ gap: 16, marginTop: 20 }}>
          <TextField label="Start date" type="date" value={startDate} onChange={(event) => { setStartDate(event.target.value); if (endDate < event.target.value) setEndDate(event.target.value); }} InputLabelProps={{ shrink: true }} />
          <TextField label="End date" type="date" value={endDate} inputProps={{ min: startDate, max: addDays(startDate, 29) }} onChange={(event) => setEndDate(event.target.value)} InputLabelProps={{ shrink: true }} helperText="Up to 30 days" />
        </Flex>
        <Flex flexWrap="wrap" style={{ gap: 16, marginTop: 20 }}>
          <TextField label="Daily start time" type="time" value={startTime} onChange={(event) => setStartTime(event.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Daily end time" type="time" value={endTime} onChange={(event) => setEndTime(event.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField select SelectProps={{ native: true }} label="Appointment duration" value={durationMinutes} onChange={(event) => setDurationMinutes(event.target.value)} InputLabelProps={{ shrink: true }}>
            {[15, 20, 30, 45, 60, 90, 120].map((minutes) => <option key={minutes} value={minutes}>{minutes} minutes</option>)}
          </TextField>
        </Flex>
        <Flex flexWrap="wrap" style={{ gap: 16, marginTop: 20 }}>
          <TextField label="Location (optional)" value={location} onChange={(event) => setLocation(event.target.value)} />
          <TextField label="Coach note (optional)" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Flex>
        <Button variant="contained" color="primary" style={{ marginTop: 28 }} disabled={createBulk.isLoading || !startDate || !endDate || !startTime || !endTime || selectedWeekdays.length === 0} onClick={() => createBulk.mutate()}>{createBulk.isLoading ? 'Creating...' : 'Create openings'}</Button>
      </SlidePage>
    </PageLayout>
  );
};

export default PrivateLessonSchedule;
